import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { STEP_PATHS, TOTAL_STEPS } from "@/modules/Quote/constants.js";
import { ArrowLeft, Icon } from "@/shared/components/Icon.jsx";
import { ProgressBar } from "@/modules/Quote/components/ProgressBar.jsx";
import QuoteSnapshotRail from "@/modules/Quote/components/QuoteSnapshotRail.jsx";
import ThemeToggle from "@/shared/components/ThemeToggle.jsx";
import HubMarketing from "@/shared/components/desktop/HubMarketing.jsx";
import HubCOIPreview from "@/shared/components/desktop/HubCOIPreview.jsx";
import MedMalGuardLanding, { MedMalGuardFooter, MedMalGuardMap, MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";

import { useStore } from "@/shared/store/useStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import modalStore from "@/shared/store/modalStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import { useIlfDlfResponse } from "@/hooks/useIlfDlfFetcher.js";
import { useSpeciality } from "@/hooks/useSpeciality.js";
import { useQuoteSnapshot } from "@/modules/Quote/utils/useQuoteSnapshot.js";
import { usePolicyStatus } from "@/modules/Payment/utils/policyStatus.js";

// Pre-signing wizard steps — only an OPEN_ORDER submission is allowed to
// sit on these via reload/back. Any other policyStatus → /dashboard.
const OPEN_ORDER_ONLY_PATHS = new Set(["/license", "/coverage", "/underwriting", "/reviewDocusign"]);

export default function FlowLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // "POP" covers browser back/forward AND a hard reload — both are the cases
  // where we re-check policyStatus before letting the user sit on /payment.
  const navigationType = useNavigationType();

  const step               = useStore(submissionStore, (s) => s.step);
  const flowSubmissionId   = useStore(submissionStore, (s) => s.flowSubmissionId);
  const sessionReady       = useStore(sessionStore, (s) => s.sessionReady);
  const bound              = useStore(sessionStore, (s) => s.bound);
  const dashView           = useStore(sessionStore, (s) => s.dashView);
  const insuredProfile     = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated    = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const ilfDlfResponse     = useIlfDlfResponse();
  const specialityMaster   = useSpeciality();
  const { phaseLabel } = useQuoteSnapshot();
  const {
    hasOrder: hasPaymentOrder,
    isLoading: paymentOrderLoading,
    isOpenOrder,
    isSigned,
  } = usePolicyStatus();

  const isReviewDocusignPath = location.pathname === "/reviewDocusign";
  // /license, /coverage, /underwriting, /reviewDocusign — on browser back or
  // reload (navigationType === "POP") only an OPEN_ORDER submission belongs
  // here. Anything else gets bounced to the dashboard. Forward navigation
  // from the wizard uses navigate(..., { replace: true }) so it surfaces as
  // "REPLACE" and skips this check.
  const shouldRedirectFromOpenOrderPath = (
    OPEN_ORDER_ONLY_PATHS.has(location.pathname)
    && navigationType === "POP"
    && !paymentOrderLoading
    && hasPaymentOrder
    && !isOpenOrder
  );
  // On /payment, back-button or reload (navigationType === "POP") is only
  // valid when the policy is in SIGNED state. Anything else (no order yet,
  // DRAFT, PAID, POLICY_ACTIVE, etc.) bounces back to the dashboard. Forward
  // navigation from /reviewDocusign uses navigate(..., { replace: true }),
  // so it shows up as "REPLACE" and skips this check.
  const shouldRedirectFromPayment = (
    location.pathname === "/payment"
    && navigationType === "POP"
    && !paymentOrderLoading
    && hasPaymentOrder
    && !isSigned
  );
  const shouldRedirectToDashboard = shouldRedirectFromOpenOrderPath || shouldRedirectFromPayment;

  // Clearing flowSubmissionId also removes the `q2b:submission:flowSubmissionId`
  // sessionStorage key (see submissionStore setter) — so a follow-up refresh
  // won't re-rehydrate the order and snap the user back into the wizard.
  useEffect(() => {
    if (shouldRedirectToDashboard && submissionStore.flowSubmissionId != null) {
      submissionStore.flowSubmissionId = null;
    }
  }, [shouldRedirectToDashboard]);

  if (shouldRedirectToDashboard) {
    return <Navigate to="/dashboard" replace />;
  }

  const goBack = () => {
    // /payment back arrow — only walk back into /reviewDocusign when the
    // order is still OPEN_ORDER. Any other policyStatus means the signing
    // window is past, so back lands on the dashboard instead.
    if (step === 7) {
      if (isOpenOrder) {
        submissionStore.step = 6;
        navigate("/reviewDocusign");
      } else {
        submissionStore.flowSubmissionId = null;
        sessionStore.dashView = "dashboard";
        navigate("/dashboard");
      }
      return;
    }
    // License/Scope/Practice (step 3) — submission is already created at
    // this point, so "back" returns to the dashboard rather than walking
    // the user back through signup.
    if (step === 3) {
      sessionStore.dashView = "dashboard";
      navigate("/dashboard");
      return;
    }
    if (step > 0) {
      const next = step - 1;
      submissionStore.step = next;
      navigate(STEP_PATHS[next]);
      return;
    }
    if (isAuthenticated) { sessionStore.dashView = "dashboard"; navigate("/dashboard"); }
    else { submissionStore.step = 0; navigate(STEP_PATHS[0]); }
  };

  // Session bootstrap loader — full-screen backdrop with centered spinner.
  if (!sessionReady && !bound) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              border: "3px solid #e5e7eb",
              borderTopColor: "var(--blue, #2563eb)",
              animation: "q2b-spin 0.8s linear infinite",
            }}
          />
          <div style={{ fontSize: 13, color: "#555", fontFamily: "'DM Sans', sans-serif", letterSpacing: 0.2 }}>
            Loading…
          </div>
          <style>{`@keyframes q2b-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (bound && dashView === null) {
    return <Navigate to="/complete-order" replace />;
  }

  // Steps 3+ (/license onward) require an authenticated insured.
  if (sessionReady && !isAuthenticated && step >= 3 && !isReviewDocusignPath) {
    return <Navigate to="/signin" replace />;
  }

  const redirectTo = isAuthenticated ? "/dashboard" : STEP_PATHS[0];
  // Pre-submission steps (/classification, /register) need a live estimate.
  if (step >= 1 && step <= 2 && !ilfDlfResponse) {
    return <Navigate to={redirectTo} replace />;
  }
  if (step === 1 && !specialityMaster) {
    return <Navigate to={redirectTo} replace />;
  }
  if (step === 3 && !ilfDlfResponse && !flowSubmissionId) {
    return <Navigate to={redirectTo} replace />;
  }
  if (step >= 4 && sessionReady && !ilfDlfResponse && !flowSubmissionId && !isReviewDocusignPath) {
    return <Navigate to={redirectTo} replace />;
  }

  // Desktop hub marketing sections render on the landing (/) only — hero
  // chrome is everywhere, but the long marketing stack is scoped to the
  // early funnel (per design scope).
  const showHubMarketing = location.pathname === "/";

  // Landing route (/): render the approved MedMalGuard "Claude1" design
  // (its own nav + calculator hero + trust band + how-it-works + need-help
  // + new dark footer) FIRST, then the original footer line, and then keep
  // ALL of the previous landing below it — the previous header (HubHeader)
  // and every prior section (HubCOIPreview + HubMarketing) render unchanged
  // beneath, so nothing that used to be on / is lost. The calculator reuses
  // the existing soft-quote flow, so step-0 wiring is unchanged.
  const isLanding = location.pathname === "/";

  return (
    <>
      {isLanding && <MedMalGuardLanding />}

      {/* MedMalGuard header for the wizard routes (steps 2–10) at EVERY
          viewport — on phones the NavBar collapses to the logo lockup +
          hamburger menu, matching the landing page. The slim mobile bar
          below keeps only back / theme / step (no branding). Skipped on
          the landing (/), which renders its own nav above. */}
      {!isLanding && (
        <div className="app-header">
          <MedMalGuardHeader />
        </div>
      )}

      {/* Wizard chrome — topbar, progress, routed step content, snapshot
          rail, and the thin footer line. On the landing (/), the new
          MedMalGuard calculator hero replaces all of it, so this block is
          skipped there; the retained marketing sections below still render. */}
      {!isLanding && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px 8px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {step > 0 ? (
                <button type="button" onClick={goBack} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}><ArrowLeft /></button>
              ) : (
                isAuthenticated ? (
                  <button type="button" onClick={() => { sessionStore.dashView = "dashboard"; navigate("/dashboard"); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}><ArrowLeft /></button>
                ) : null
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ThemeToggle />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.05em" }}>{phaseLabel}</div>
                <div style={{ fontSize: 10, color: "#999" }}>Step {step + 1} of {TOTAL_STEPS}</div>
              </div>
            </div>
          </div>
          <div style={{ padding: "0 18px" }}><ProgressBar /></div>

          <div style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {/* Desktop-only back control. The mobile topbar above carries its
                own arrow, but `.hub-desktop`'s sibling rule in responsive.css
                hides that whole bar >=1024px and MedMalGuardHeader (which
                replaces it) has no back affordance — so without this the
                wizard has no way back on desktop. Wrapped in `.hub-desktop`
                so mobile doesn't get a second arrow. Shares goBack() with the
                topbar, keeping the step-7/step-3 rules in one place. */}
            <div className="hub-desktop">
              <BackControl onClick={goBack} />
            </div>
            <Outlet />
          </div>
          <QuoteSnapshotRail />

          {/* Footer keeps ONLY the three contact affordances: phone, email,
              and the About modal. Privacy/Terms remain reachable from the
              in-page legal links (e.g. registration consent line). */}
          <div className="wizard-footer-line" style={{ padding: "6px 16px", borderTop: "1px solid #f0f0f0", textAlign: "center", fontSize: 10, color: "#ccc" }}>
            <a href="tel:+18889663881" style={{ color: "inherit", textDecoration: "none" }}>(888) 966-3881</a> ·{" "}
            <a href="mailto:support@selectfirstinsurance.com" style={{ color: "inherit", textDecoration: "none" }}>support@selectfirstinsurance.com</a> ·{" "}
            <LegalLink onClick={() => { modalStore.showAbout = true; }}>About us</LegalLink>
          </div>
        </>
      )}

      {/* Affordable Coverage / Certificate in Minutes map section — on the
          landing route it renders BEFORE the marketing sections below. */}
      {isLanding && <MedMalGuardMap />}

      {/* Desktop-only hub marketing + COI previewer + footer. Hidden
          < 1024px via `.hub-desktop`; landing/soft-quote routes only. */}
      {showHubMarketing ? (
        <>
          <HubCOIPreview />
          <HubMarketing />
        </>
      ) : null}

      {/* New MedMalGuard dark footer (renders nothing now; kept as the
          landing's final slot). */}
      {isLanding && <MedMalGuardFooter />}

    </>
  );
}

// Desktop back control for the wizard form column. Icon-only would match the
// mobile topbar, but at the top of a wide content column a bare 18px grey
// glyph reads as decoration — so this pairs the same ArrowLeft with a "Back"
// label. Hover shifts to --blue, matching the wordmark's phone link.
function BackControl({ onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        alignSelf: "flex-start",
        background: "none",
        border: "none",
        // Left-aligns the arrow with the column edge while keeping a
        // comfortable click target.
        padding: "0 8px 0 0",
        marginBottom: 16,
        cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: 0.2,
        color: hover ? "var(--blue)" : "#888",
        transition: "color 0.15s ease",
      }}
    >
      {/* Base Icon rather than the ArrowLeft wrapper — ArrowLeft hardcodes
          stroke="#888", which would leave the glyph grey while the label
          turns blue on hover. */}
      <Icon
        size={18}
        stroke={hover ? "var(--blue)" : "#888"}
        d={<><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></>}
      />
      Back
    </button>
  );
}

export function BrandWordmark() {
  const legalLink = {
    fontSize: 11,
    color: "#aaa",
    cursor: "pointer",
  };
  const legalSep = { fontSize: 11, color: "#ddd" };
  return (
    <span
      className="brand-wordmark"
      style={{
        flexDirection: "column",
        lineHeight: 1.15,
        marginLeft: 4,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>
        SelectFirst Insurance
      </span>
      <span style={{ display: "inline-flex", alignItems: "baseline", gap: 48 }}>
        <a
          href="tel:+18889663881"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--blue)",
            textDecoration: "none",
          }}
        >
          (888) 966-3881
        </a>
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
          <LegalLink onClick={() => { modalStore.showAbout = true; }} style={legalLink}>About</LegalLink>
          <span style={legalSep}>·</span>
          <LegalLink onClick={() => { modalStore.showPrivacy = true; }} style={legalLink}>Privacy</LegalLink>
          <span style={legalSep}>·</span>
          <LegalLink onClick={() => { modalStore.showTerms = true; }} style={legalLink}>Terms</LegalLink>
        </span>
      </span>
    </span>
  );
}

// Renders the same underlined-link visual as the previous clickable
// <span>, but as a real <button> so keyboard / screen-reader users can
// actually trigger it. Visual parity is preserved (no border, no
// background, inherits inline color/font), so the user journey looks
// identical.
function LegalLink({ onClick, children, style }) {
  const baseStyle = {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    color: style?.color || "#aaa",
    textDecoration: "underline",
  };
  return (
    <button type="button" onClick={onClick} style={{ ...baseStyle, ...(style || {}) }}>
      {children}
    </button>
  );
}
